/**
 * ActeursLocauxSection — annuaire des acteurs locaux côté administré.
 *
 * Consultation de l'annuaire économique de la commune (commerces, artisans,
 * producteurs). Pour les producteurs, affiche les paniers/produits en circuit
 * court. Mise en relation seulement (contact direct, pas de paiement en ligne).
 * Autonome : charge ses propres données. Masquage auto si aucun acteur publié.
 */

import { useEffect, useState, useCallback } from 'react';
import { Badge, Alert, Button, Field, Input } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { ActeurLocal, ActeurCategorie } from '@/lib/api';
import { Store, MapPin, Phone, Mail, Globe, Clock, Package, AlertCircle, Sprout, Plus, CheckCircle2 } from 'lucide-react';

const CATEGORIE_LABEL: Record<ActeurCategorie, string> = {
  producteur: 'Producteur', commerce: 'Commerce', artisan: 'Artisan', service: 'Service',
  restauration: 'Restauration', sante: 'Santé', profession_liberale: 'Profession libérale', autre: 'Autre',
};
const CATEGORIE_VARIANT: Record<ActeurCategorie, 'success' | 'info' | 'warning' | 'neutral'> = {
  producteur: 'success', commerce: 'info', artisan: 'warning', service: 'neutral',
  restauration: 'warning', sante: 'info', profession_liberale: 'neutral', autre: 'neutral',
};

function euros(v: number | null): string {
  if (v == null) return '';
  return `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function ActeursLocauxSection() {
  const [acteurs, setActeurs] = useState<ActeurLocal[] | null>(null);
  const [mesDeclarations, setMesDeclarations] = useState<ActeurLocal[]>([]);
  const [filtre, setFiltre] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [res, decl] = await Promise.all([
        api.acteursLocaux.list({ categorie: filtre || undefined }),
        api.acteursLocaux.mesDeclarations().catch(() => ({ declarations: [] })),
      ]);
      setActeurs(res?.acteurs ?? []);
      setMesDeclarations(decl.declarations ?? []);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, [filtre]);
  useEffect(() => { void reload(); }, [reload]);

  const selectCls = 'rounded-r-md border border-border bg-bg px-3 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  // Bloc « se déclarer » + suivi de mes déclarations, réutilisé quel que soit
  // l'état de l'annuaire (même s'il est vide, un habitant peut se déclarer).
  const declarationBlock = (
    <DeclarationBlock
      showForm={showForm} setShowForm={setShowForm}
      mesDeclarations={mesDeclarations}
      onDeclared={() => { setShowForm(false); void reload(); }}
    />
  );

  if (!loading && (acteurs === null || acteurs.length === 0) && !filtre) {
    return (
      <div>
        <p className="text-sm text-ink-muted">Aucun acteur local référencé pour l'instant.</p>
        <div className="mt-3">{declarationBlock}</div>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-3 flex items-center gap-2">
        <select className={selectCls} value={filtre} onChange={(e) => setFiltre(e.target.value)}>
          <option value="">Tous</option>
          {Object.entries(CATEGORIE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="text-xs text-ink-dim">Contactez directement les acteurs pour commander.</span>
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : (acteurs?.length ?? 0) === 0 ? (
        <p className="text-sm text-ink-muted">Aucun acteur dans cette catégorie.</p>
      ) : (
        <ul className="space-y-2.5">
          {acteurs!.map((a) => (
            <li key={a.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
              <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                <span>{a.nom}</span>
                <Badge variant={CATEGORIE_VARIANT[a.categorie]} size="sm">{CATEGORIE_LABEL[a.categorie]}</Badge>
              </div>
              {a.activite && <div className="mt-0.5 text-sm text-ink-muted">{a.activite}</div>}
              {a.description && <p className="mt-0.5 text-sm text-ink-muted">{a.description}</p>}
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-dim">
                {a.telephone && <a href={`tel:${a.telephone}`} className="inline-flex items-center gap-1 hover:text-grc"><Phone className="h-3 w-3" />{a.telephone}</a>}
                {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-grc"><Mail className="h-3 w-3" />{a.email}</a>}
                {a.siteWeb && <a href={a.siteWeb} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-grc"><Globe className="h-3 w-3" />Site web</a>}
                {(a.adresse || a.ville) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[a.adresse, a.ville].filter(Boolean).join(', ')}</span>}
                {a.horaires && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{a.horaires}</span>}
              </div>

              {/* Produits (producteurs) */}
              {(a.produits?.length ?? 0) > 0 && (
                <div className="mt-2 rounded-r-md border border-grc/20 bg-grc-tint/30 p-2.5">
                  <div className="mb-1.5 inline-flex items-center gap-1 text-xs font-medium text-grc-fg">
                    <Sprout className="h-3.5 w-3.5" /> Produits en circuit court
                  </div>
                  <ul className="space-y-1">
                    {a.produits!.map((p) => (
                      <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-ink">
                          <Package className="mr-1 inline h-3 w-3 text-ink-dim" />{p.nom}
                          {p.saison ? <span className="ml-1 text-xs text-ink-dim">({p.saison})</span> : null}
                        </span>
                        {p.prixEuros != null && (
                          <span className="whitespace-nowrap font-medium text-ink">{euros(p.prixEuros)}{p.unite ? <span className="text-xs text-ink-dim"> / {p.unite}</span> : null}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5">{declarationBlock}</div>
    </div>
  );
}

// ============================================================================
// Bloc « se déclarer comme acteur local » + suivi de mes déclarations
// ============================================================================
const STATUT_LABEL: Record<'valide' | 'en_attente' | 'refuse', { label: string; variant: 'success' | 'warning' | 'urgent' }> = {
  valide: { label: 'Publiée', variant: 'success' },
  en_attente: { label: 'En attente de validation', variant: 'warning' },
  refuse: { label: 'Refusée', variant: 'urgent' },
};

function DeclarationBlock({ showForm, setShowForm, mesDeclarations, onDeclared }: {
  showForm: boolean; setShowForm: (v: boolean) => void;
  mesDeclarations: ActeurLocal[]; onDeclared: () => void;
}) {
  return (
    <div className="rounded-r-lg border border-dashed border-border bg-surface/40 p-4">
      {mesDeclarations.length > 0 && (
        <div className="mb-3">
          <div className="mb-1.5 text-2xs font-medium uppercase tracking-r-eyebrow text-ink-dim">Mes demandes de référencement</div>
          <ul className="space-y-1.5">
            {mesDeclarations.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/60 px-3 py-2 text-sm">
                <span className="min-w-0 truncate text-ink">{d.nom}</span>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <Badge variant={STATUT_LABEL[d.statutValidation].variant} size="sm">{STATUT_LABEL[d.statutValidation].label}</Badge>
                  {d.statutValidation === 'refuse' && d.motifRefus && <span className="text-xs text-ink-dim">{d.motifRefus}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!showForm ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-ink-muted">
            <span className="font-medium text-ink">Vous êtes commerçant, artisan ou producteur&nbsp;?</span> Faites-vous référencer dans l'annuaire de la commune.
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowForm(true)}><Plus className="h-4 w-4" /> Me référencer</Button>
        </div>
      ) : (
        <DeclarationForm onCancel={() => setShowForm(false)} onDone={onDeclared} />
      )}
    </div>
  );
}

function DeclarationForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [nom, setNom] = useState('');
  const [categorie, setCategorie] = useState<ActeurCategorie>('commerce');
  const [activite, setActivite] = useState('');
  const [description, setDescription] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [adresse, setAdresse] = useState('');
  const [ville, setVille] = useState('');
  const [siteWeb, setSiteWeb] = useState('');
  const [horaires, setHoraires] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  async function envoyer() {
    setBusy(true); setErr(null);
    try {
      await api.acteursLocaux.declarer({
        nom: nom.trim(), categorie, activite: activite.trim() || null, description: description.trim() || null,
        telephone: telephone.trim() || null, email: email.trim() || null, adresse: adresse.trim() || null,
        ville: ville.trim() || null, siteWeb: siteWeb.trim() || null, horaires: horaires.trim() || null,
      });
      setOk(true);
      setTimeout(onDone, 1200);
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Envoi impossible'); setBusy(false); }
  }

  if (ok) {
    return (
      <Alert variant="success" icon={CheckCircle2}>
        Votre demande a été envoyée. La mairie la validera avant publication dans l'annuaire.
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
      <div className="text-sm font-medium text-ink">Demande de référencement</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nom de l'établissement" htmlFor="d-nom" required><Input id="d-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ferme des Trois Chênes" /></Field>
        <Field label="Catégorie" htmlFor="d-cat" required>
          <select id="d-cat" className={selectCls} value={categorie} onChange={(e) => setCategorie(e.target.value as ActeurCategorie)}>
            {Object.entries(CATEGORIE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Activité" htmlFor="d-act"><Input id="d-act" value={activite} onChange={(e) => setActivite(e.target.value)} placeholder="Maraîchage bio, boulangerie…" /></Field>
      <Field label="Description" htmlFor="d-desc">
        <textarea id="d-desc" rows={2} className={selectCls} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Téléphone" htmlFor="d-tel"><Input id="d-tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} /></Field>
        <Field label="Email" htmlFor="d-email"><Input id="d-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Adresse" htmlFor="d-adr"><Input id="d-adr" value={adresse} onChange={(e) => setAdresse(e.target.value)} /></Field>
        <Field label="Ville" htmlFor="d-ville"><Input id="d-ville" value={ville} onChange={(e) => setVille(e.target.value)} /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Site web" htmlFor="d-web"><Input id="d-web" value={siteWeb} onChange={(e) => setSiteWeb(e.target.value)} placeholder="https://…" /></Field>
        <Field label="Horaires" htmlFor="d-hor"><Input id="d-hor" value={horaires} onChange={(e) => setHoraires(e.target.value)} placeholder="Mar-Sam 8h-13h" /></Field>
      </div>
      <p className="text-xs text-ink-dim">Votre demande sera examinée par la mairie avant publication dans l'annuaire.</p>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button variant="primary" size="sm" onClick={envoyer} disabled={busy || nom.trim().length < 2}>{busy ? 'Envoi…' : 'Envoyer ma demande'}</Button>
      </div>
    </div>
  );
}
